import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import s from "./SchedulePicker.module.css";

const DAYS = [
  { key: "Mon", label: "Mon", full: "Monday" },
  { key: "Tue", label: "Tue", full: "Tuesday" },
  { key: "Wed", label: "Wed", full: "Wednesday" },
  { key: "Thu", label: "Thu", full: "Thursday" },
  { key: "Fri", label: "Fri", full: "Friday" },
  { key: "Sat", label: "Sat", full: "Saturday" },
];
const DAY_KEYS  = DAYS.map(d => d.key);
const DAY_LOOKUP = new Map(DAYS.flatMap(d => [
  [d.key.toLowerCase(), d.key],
  [d.full.toLowerCase(), d.key],
  [d.key[0].toLowerCase(), d.key], // "M", "T", "W" — ambiguous on purpose
]));

/** Parse a "Mon/Wed 08:00-09:00" style string. Tolerant of minor variations
 *  so legacy free-form entries still load cleanly; if the parser can't make
 *  sense of something, it leaves the fields blank and shows the raw string
 *  as a fallback note so the admin can fix it by hand. Also accepts an
 *  optional seconds component ("08:00:30-09:00:30"). */
export function parseSchedule(raw) {
  const out = { days: [], start: "", end: "", leftover: "" };
  if (!raw) return out;
  const trimmed = String(raw).trim();
  if (!trimmed) return out;

  // Split on the last whitespace to separate day tokens from the time range.
  const lastSpace = trimmed.lastIndexOf(" ");
  const daysPart  = lastSpace === -1 ? trimmed : trimmed.slice(0, lastSpace).trim();
  const timesPart = lastSpace === -1 ? ""      : trimmed.slice(lastSpace + 1).trim();

  // Try to parse the time range first; if the last token doesn't look like
  // a range, the whole thing is probably days only.
  const rangeMatch = timesPart.match(/^(\d{1,2}:\d{2}(?::\d{2})?)\s*[-–]\s*(\d{1,2}:\d{2}(?::\d{2})?)$/);
  if (rangeMatch) {
    out.start = padTime(rangeMatch[1]);
    out.end   = padTime(rangeMatch[2]);
  } else if (timesPart) {
    // Couldn't parse — fall back to treating the whole string as days, and
    // note the leftover so the user can recover it manually.
    out.leftover = timesPart;
  }

  // Day tokens: split on "/", ",", or whitespace and look up known names.
  const dayTokens = (rangeMatch ? daysPart : trimmed)
    .split(/[\s,/]+/)
    .map(t => t.toLowerCase())
    .filter(Boolean);
  for (const tok of dayTokens) {
    const hit = DAY_LOOKUP.get(tok);
    if (hit && !out.days.includes(hit)) out.days.push(hit);
  }
  // Preserve Mon→Sat order for display consistency.
  out.days.sort((a, b) => DAY_KEYS.indexOf(a) - DAY_KEYS.indexOf(b));
  return out;
}

function padTime(t) {
  const parts = t.split(":");
  const h = (parts[0] || "").padStart(2, "0");
  const m = (parts[1] || "00").padStart(2, "0");
  const s = parts[2] ? parts[2].padStart(2, "0") : null;
  return s ? `${h}:${m}:${s}` : `${h}:${m}`;
}

export function formatSchedule({ days, start, end }) {
  const d = days.join("/");
  const t = (start && end) ? `${start}-${end}` : (start || end || "");
  return [d, t].filter(Boolean).join(" ");
}

function isBefore(a, b) {
  return a && b && a.localeCompare(b) < 0;
}

/** Cheap equality check for the day-preset highlight. Used by MWF / TTh /
 *  Weekdays / Saturday buttons to know when their preset is the *exact*
 *  current selection (so we can flip them into an "active" visual state). */
function arraysEqual(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

function diffMinutes(start, end) {
  if (!start || !end) return null;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  if ([sh, sm, eh, em].some(Number.isNaN)) return null;
  return (eh * 60 + em) - (sh * 60 + sm);
}

/** True when the current start/end span exactly the given minutes —
 *  used to highlight the matching duration chip. */
function matchesDuration(start, end, minutes) {
  return diffMinutes(start, end) === minutes;
}

/** Friendly "1 hr 30 min" string for the Duration label hint. Shows
 *  the current span if start/end are both set; empty otherwise. */
function currentDurationLabel(start, end) {
  const d = diffMinutes(start, end);
  if (d == null || d <= 0) return "";
  const h = Math.floor(d / 60);
  const m = d % 60;
  if (h && m) return `${h} hr ${m} min`;
  if (h)      return `${h} hr`;
  return `${m} min`;
}

/** Hour options in the dropdowns — 7 AM to 6 PM covers every practical
 *  school schedule. 24-hour format keeps the internal string format
 *  compatible with everything else in the app. */
const HOURS   = ["07","08","09","10","11","12","13","14","15","16","17","18"];
/** 5-minute increments — enough granularity for real schedules, compact
 *  enough to fit in a native <select> without scrolling. */
const MINUTES = ["00","05","10","15","20","25","30","35","40","45","50","55"];

/** Two-column time picker — Start on the left, End on the right, both
 *  with paired hour/minute selects + an AM/PM badge. Emits "HH:MM"
 *  24-hour strings to stay compatible with the rest of the app. */
function TimeSection({ start, end, onStartChange, onEndChange }) {
  return (
    <div className={s.timeBlock}>
      <div className={s.timeBlockHead}>
        <span className={s.timeBlockLabel}>Start</span>
        <span className={s.timeArrow} aria-hidden>→</span>
        <span className={s.timeBlockLabel}>End</span>
      </div>
      <div className={s.timeBlockGrid}>
        <TimeCard value={start} onChange={onStartChange} />
        <TimeCard value={end}   onChange={onEndChange}   />
      </div>
    </div>
  );
}

function ampmFor(hour24) {
  const h = parseInt(hour24, 10);
  if (Number.isNaN(h)) return "";
  return h >= 12 ? "PM" : "AM";
}

/** Small custom dropdown (never a native <select>) for the Hour/Minute
 *  pickers inside TimeCard.
 *
 *  Why not a native <select>: this control lives inside SchedulePicker's
 *  own popup, which is itself `position: fixed` with JS-computed
 *  coordinates so it can escape a modal's `overflow: auto` clipping.
 *  Nesting an invisible native <select> inside THAT (the previous
 *  approach — a transparent select layered over a styled readout) made
 *  some browsers badly miscalculate where to anchor the native option
 *  list: it could render as a tall, blank, disconnected box far from
 *  the field, overlapping unrelated page chrome. That's a browser/OS
 *  rendering quirk around nested fixed-position contexts, not something
 *  fixable with CSS on the select. Rendering the option list ourselves,
 *  positioned with ordinary `position: absolute` relative to THIS
 *  component's own root, sidesteps the whole class of bug — there's no
 *  native popup for the browser to mis-place. */
function MiniSelect({ value, options, placeholder, onChange, ariaLabel }) {
  const [open, setOpen] = useState(false);
  // All 12 hour/minute options render as a compact grid now (no
  // scrolling needed — see the list's CSS), so the only positioning
  // question left is which SIDE of the trigger it opens on. Flips
  // upward when there isn't comfortable room below — this TimeCard can
  // sit near the bottom of the popup, which can itself sit near the
  // bottom of the viewport, and a dropdown that opens into a wall of
  // other controls (or off-screen) is the opposite of user-friendly.
  const [openUpward, setOpenUpward] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e) {
      if (rootRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    function onKey(e) { if (e.key === "Escape") setOpen(false); }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Measured before paint so the list never visibly opens downward and
  // then jumps upward a frame later.
  useLayoutEffect(() => {
    if (!open) return;
    const trigger = rootRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const estimatedListHeight = 140; // 3 rows + padding, with headroom
    const roomBelow = window.innerHeight - rect.bottom;
    setOpenUpward(roomBelow < estimatedListHeight && rect.top > roomBelow);
  }, [open]);

  return (
    <div className={s.miniSelect} ref={rootRef}>
      <button
        type="button"
        className={s.miniSelectTrigger}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={e => { e.stopPropagation(); setOpen(o => !o); }}>
        {value || placeholder}
      </button>
      {open && (
        <ul
          className={`${s.miniSelectList} ${openUpward ? s.miniSelectListUp : ""}`}
          role="listbox"
          aria-label={ariaLabel}>
          {options.map(opt => (
            <li key={opt} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={opt === value}
                data-value={opt}
                className={`${s.miniSelectOption} ${opt === value ? s.miniSelectOptionActive : ""}`}
                onClick={e => { e.stopPropagation(); onChange(opt); setOpen(false); }}>
                {opt}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Compact time card — large hour:minute readout where the hour and
 *  minute digits are themselves the MiniSelect triggers, so it still
 *  reads as one big tappable readout even though each half opens its
 *  own small dropdown. */
function TimeCard({ value, onChange }) {
  const [h = "", m = ""] = (value || "").split(":");
  const ampm = ampmFor(h);
  function setHour(v)   { onChange(v ? `${v}:${m || "00"}` : ""); }
  function setMinute(v) { onChange(h ? `${h}:${v || "00"}` : (v ? `08:${v}` : "")); }
  const isSet = !!h;
  return (
    <div className={`${s.timeCard} ${isSet ? s.timeCardSet : ""}`}>
      <div className={s.timeCardReadout}>
        <MiniSelect value={h} options={HOURS}   placeholder="--" onChange={setHour}   ariaLabel="Hour" />
        <span className={s.timeCardColon}>:</span>
        <MiniSelect value={m} options={MINUTES} placeholder="--" onChange={setMinute} ariaLabel="Minute" />
        {ampm && <span className={s.timeCardAmpm}>{ampm}</span>}
      </div>
    </div>
  );
}

/**
 * Controlled input that looks like a single text field but opens a little
 * calendar-style popup with day pills + start/end time pickers. Always emits
 * the canonical "Mon/Wed 08:00-09:00" string via onChange so the backend and
 * the rest of the app don't need to care that the UI changed.
 */
export default function SchedulePicker({ value, onChange, placeholder = "Mon/Wed 08:00-09:00" }) {
  const [open,   setOpen]   = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 320 });
  const rootRef  = useRef(null);
  const popupRef = useRef(null);

  /** Recompute the popup position relative to the trigger. Runs when we open
   *  and on scroll/resize so the popup tracks if the container moves. Uses
   *  position:fixed so it escapes the modal's overflow:auto clipping. */
  function recomputeCoords() {
    const el = rootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const popupMinWidth = 320;
    const width = Math.max(popupMinWidth, rect.width);
    // Right-edge guard — shift the popup leftward if it would overflow.
    const overshoot = (rect.left + width) - (window.innerWidth - 8);
    const left = Math.max(8, rect.left - Math.max(0, overshoot));
    setCoords({ top: rect.bottom + 6, left, width });
  }

  // Derived parsed form. Re-parses whenever the external value changes, so
  // programmatic updates (e.g. clearing after save) stay in sync.
  const parsed = useMemo(() => parseSchedule(value), [value]);
  const [days,  setDays]  = useState(parsed.days);
  const [start, setStart] = useState(parsed.start);
  const [end,   setEnd]   = useState(parsed.end);

  useEffect(() => {
    if (!open) {
      setDays(parsed.days);
      setStart(parsed.start);
      setEnd(parsed.end);
    }
  }, [parsed, open]);

  // Outside-click to dismiss the popup. Because the popup now renders with
  // position:fixed it's outside rootRef's DOM tree — check popupRef too.
  useEffect(() => {
    if (!open) return;
    function onDoc(e) {
      if (rootRef.current?.contains(e.target))  return;
      if (popupRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Keep the popup anchored while the page / modal scrolls or resizes.
  useEffect(() => {
    if (!open) return;
    recomputeCoords();
    window.addEventListener("resize", recomputeCoords);
    window.addEventListener("scroll",  recomputeCoords, true);
    return () => {
      window.removeEventListener("resize", recomputeCoords);
      window.removeEventListener("scroll",  recomputeCoords, true);
    };
  }, [open]);

  function commit(nextDays, nextStart, nextEnd) {
    const out = formatSchedule({ days: nextDays, start: nextStart, end: nextEnd });
    onChange(out);
  }

  function toggleDay(key) {
    const next = days.includes(key)
      ? days.filter(x => x !== key)
      : [...days, key].sort((a, b) => DAY_KEYS.indexOf(a) - DAY_KEYS.indexOf(b));
    setDays(next);
    commit(next, start, end);
  }

  function setStartTime(v) {
    // Smart fill: when the user picks a start time and end is empty,
    // pre-fill end as start + 1h. Saves one interaction in the most
    // common case (typical class duration). User can still change end.
    let nextEnd = end;
    if (v && !end) {
      const [h, m] = v.split(":").map(Number);
      if (!Number.isNaN(h)) {
        const eh = Math.min(23, h + 1);
        nextEnd = `${String(eh).padStart(2, "0")}:${String(m || 0).padStart(2, "0")}`;
        setEnd(nextEnd);
      }
    }
    setStart(v);
    commit(days, v, nextEnd);
  }
  function setEndTime(v) {
    setEnd(v);
    commit(days, start, v);
  }

  /** Apply a duration chip — sets end to start + N minutes. If start
   *  is empty, default it to 08:00 first so the chip is still useful
   *  on a fresh entry. */
  function applyDuration(minutes) {
    let nextStart = start || "08:00";
    const [h, m] = nextStart.split(":").map(Number);
    const totalMin = h * 60 + (m || 0) + minutes;
    const eh = Math.min(23, Math.floor(totalMin / 60));
    const em = totalMin % 60;
    const nextEnd = `${String(eh).padStart(2, "0")}:${String(em).padStart(2, "0")}`;
    setStart(nextStart);
    setEnd(nextEnd);
    commit(days, nextStart, nextEnd);
  }

  function clearAll() {
    setDays([]); setStart(""); setEnd("");
    onChange("");
  }

  const isEmpty = !value;

  return (
    <div className={s.root} ref={rootRef}>
      <button
        type="button"
        className={`${s.trigger} ${isEmpty ? s.triggerEmpty : ""}`}
        onClick={() => {
          if (!open) recomputeCoords();
          setOpen(o => !o);
        }}>
        {/* Calendar icon on the left */}
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none"
             stroke="currentColor" strokeWidth="1.7"
             strokeLinecap="round" strokeLinejoin="round"
             className={s.triggerIcon} aria-hidden>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M3 9h18M8 3v4M16 3v4" />
        </svg>
        <span className={s.triggerText}>{value || placeholder}</span>
        <span className={s.triggerChevron} aria-hidden>▾</span>
      </button>

      {open && (
        <div
          className={s.popup}
          ref={popupRef}
          role="dialog"
          style={{ top: coords.top, left: coords.left, minWidth: coords.width }}>
          {/* Days — bigger refined pills with stagger-fade animation. */}
          <div className={s.popupSection}>
            <div className={s.popupLabel}>
              <span>Days</span>
              {days.length > 0 && (
                <span className={s.popupLabelHint}>
                  {days.length} day{days.length === 1 ? "" : "s"} selected
                </span>
              )}
            </div>
            <div className={s.dayRow}>
              {DAYS.map((d, i) => (
                <button
                  key={d.key}
                  type="button"
                  className={`${s.dayBtn} ${days.includes(d.key) ? s.dayBtnActive : ""}`}
                  onClick={() => toggleDay(d.key)}
                  title={d.full}
                  style={{ "--day-i": i }}>
                  <span className={s.dayBtnLabel}>{d.label}</span>
                  <span className={s.dayBtnFull}>{d.full.slice(0, 3)}</span>
                </button>
              ))}
            </div>
            <div className={s.dayShortcuts}>
              <button type="button"
                      className={`${s.quickBtn} ${arraysEqual(days, ["Mon","Wed","Fri"]) ? s.quickBtnActive : ""}`}
                      onClick={() => { const d = ["Mon","Wed","Fri"]; setDays(d); commit(d, start, end); }}>
                MWF
              </button>
              <button type="button"
                      className={`${s.quickBtn} ${arraysEqual(days, ["Tue","Thu"]) ? s.quickBtnActive : ""}`}
                      onClick={() => { const d = ["Tue","Thu"]; setDays(d); commit(d, start, end); }}>
                TTh
              </button>
              <button type="button"
                      className={`${s.quickBtn} ${arraysEqual(days, ["Mon","Tue","Wed","Thu","Fri"]) ? s.quickBtnActive : ""}`}
                      onClick={() => { const d = ["Mon","Tue","Wed","Thu","Fri"]; setDays(d); commit(d, start, end); }}>
                Weekdays
              </button>
              <button type="button"
                      className={`${s.quickBtn} ${arraysEqual(days, ["Sat"]) ? s.quickBtnActive : ""}`}
                      onClick={() => { const d = ["Sat"]; setDays(d); commit(d, start, end); }}>
                Saturday
              </button>
            </div>
          </div>

          {/* Time — premium two-card readout. */}
          <div className={s.popupSection}>
            <div className={s.popupLabel}>Time</div>
            <TimeSection
              start={start}
              end={end}
              onStartChange={setStartTime}
              onEndChange={setEndTime}
            />
          </div>

          {/* Duration shortcuts — apply common class lengths in one tap.
              Highlights the matching duration when start/end already
              line up so the user can see "this is a 1h block" at a glance. */}
          <div className={s.popupSection}>
            <div className={s.popupLabel}>
              <span>Duration</span>
              <span className={s.popupLabelHint}>{currentDurationLabel(start, end)}</span>
            </div>
            <div className={s.durationRow}>
              {[
                { mins: 30,  label: "30 min" },
                { mins: 45,  label: "45 min" },
                { mins: 60,  label: "1 hr"   },
                { mins: 90,  label: "1.5 hr" },
                { mins: 120, label: "2 hr"   },
              ].map(opt => {
                const active = matchesDuration(start, end, opt.mins);
                return (
                  <button
                    key={opt.mins}
                    type="button"
                    className={`${s.durationBtn} ${active ? s.durationBtnActive : ""}`}
                    onClick={() => applyDuration(opt.mins)}>
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {start && end && isBefore(end, start) && (
            <div className={s.warn}>End time is earlier than start — double-check.</div>
          )}

          {parsed.leftover && (
            <div className={s.leftover}>
              Couldn't parse <code>{parsed.leftover}</code> from the old value — pick the time manually.
            </div>
          )}

          <div className={s.popupFoot}>
            <button type="button" className={s.clearBtn} onClick={clearAll}>Clear</button>
            <button type="button" className={s.doneBtn} onClick={() => setOpen(false)}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}
